import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, FileText } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/use-auth';
import { cn } from '@/lib/utils';

interface ReportRow {
  userId: string;
  fullName: string;
  employeeCode: string | null;
  date: string;
  weekday: number;
  isWeekend: boolean;
  isHoliday: boolean;
  holidayName: string | null;
  expectedMinutes: number;
  workedMinutes: number;
  breakMinutes: number;
  balanceMinutes: number;
  status: 'OPEN' | 'CLOSED' | 'INCIDENT' | null;
  absenceTypeCode: string | null;
  absenceTypeName: string | null;
  isHalfDayAbsence: boolean;
  halfDayPart: 'AM' | 'PM' | null;
}

interface ReportResponse {
  year: number;
  month: number;
  rows: ReportRow[];
}

interface UserOption {
  id: string;
  fullName: string;
  email: string;
}

const MONTH_NAMES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

function defaultMonth(): { year: number; month: number } {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { year: prev.getFullYear(), month: prev.getMonth() + 1 };
}

export function Reports() {
  const { user } = useAuth();
  const [{ year, month }, setPeriod] = useState(() => defaultMonth());
  const [userId, setUserId] = useState<string>('');
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const teamQuery = useQuery({
    queryKey: ['team', 'as-options'],
    queryFn: () => api<UserOption[]>('/team'),
  });

  const reportQuery = useQuery({
    queryKey: ['reports', 'monthly', year, month, userId || null],
    queryFn: () =>
      api<ReportResponse>(
        `/reports/monthly?year=${year}&month=${month}` + (userId ? `&userId=${userId}` : ''),
      ),
  });

  const monthOptions = useMemo(() => {
    const opts: { year: number; month: number; label: string }[] = [];
    const now = new Date();
    for (let i = 0; i < 13; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      opts.push({
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
      });
    }
    return opts;
  }, []);

  async function downloadFile(format: 'csv' | 'pdf') {
    setDownloadError(null);
    setDownloading(true);
    try {
      const url =
        `${API_BASE_URL}/reports/monthly?year=${year}&month=${month}&format=${format}` +
        (userId ? `&userId=${userId}` : '');
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        let detail: string | undefined;
        try {
          const body = (await res.json()) as { message?: string | string[] };
          detail = Array.isArray(body.message) ? body.message[0] : body.message;
        } catch {
          // El error puede no ser JSON
        }
        throw new Error(detail ? `${detail} (HTTP ${res.status})` : `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const fallback =
        format === 'pdf'
          ? `registro-horario_${year}-${String(month).padStart(2, '0')}.pdf`
          : `reporte_${year}-${String(month).padStart(2, '0')}.csv`;
      const filename =
        res.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] ?? fallback;
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (err) {
      setDownloadError(
        err instanceof Error ? err.message : `Error descargando ${format.toUpperCase()}`,
      );
    } finally {
      setDownloading(false);
    }
  }

  const rows = useMemo(() => reportQuery.data?.rows ?? [], [reportQuery.data]);
  const previewRows = rows.slice(0, 50);

  const totals = useMemo(() => {
    let expected = 0;
    let worked = 0;
    let balance = 0;
    let incidents = 0;
    let absences = 0;
    for (const r of rows) {
      expected += r.expectedMinutes;
      worked += r.workedMinutes;
      balance += r.balanceMinutes;
      if (r.status === 'INCIDENT') incidents++;
      if (r.absenceTypeCode) absences++;
    }
    return { expected, worked, balance, incidents, absences };
  }, [rows]);

  const isManagerOnly = user?.role === 'MANAGER';
  const periodLabel = `${MONTH_NAMES[month - 1]} ${year}`;

  return (
    <div className="p-6 md:p-8">
      <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden">
        <header className="px-7 py-5 border-b border-border flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold tracking-tightish capitalize">
              Reporte mensual · {periodLabel}
            </h2>
            <p className="text-[13px] text-muted-foreground mt-1">
              Una fila por empleado y día (festivos y ausencias incluidos).
              {isManagerOnly ? ' Como manager sólo ves a tus reports directos.' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => downloadFile('csv')}
              disabled={downloading || rows.length === 0}
            >
              <Download className="h-3.5 w-3.5" />
              {downloading ? 'Descargando…' : 'CSV'}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => downloadFile('pdf')}
              disabled={downloading || rows.length === 0}
            >
              <FileText className="h-3.5 w-3.5" />
              {downloading ? 'Descargando…' : 'PDF mensual'}
            </Button>
          </div>
        </header>

        <div className="px-7 py-4 border-b border-border bg-paper/40">
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rep-mes">Mes</Label>
              <select
                id="rep-mes"
                value={`${year}-${month}`}
                onChange={(e) => {
                  const [y, m] = e.target.value.split('-').map(Number);
                  setPeriod({ year: y, month: m });
                }}
                className="h-9 w-full rounded-sm border border-input bg-card px-3 text-base text-foreground focus-visible:border-icaria-600 focus-visible:outline-none transition-colors"
              >
                {monthOptions.map((o) => (
                  <option key={`${o.year}-${o.month}`} value={`${o.year}-${o.month}`}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rep-emp">Empleado</Label>
              <select
                id="rep-emp"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                disabled={teamQuery.isLoading}
                className="h-9 w-full rounded-sm border border-input bg-card px-3 text-base text-foreground focus-visible:border-icaria-600 focus-visible:outline-none transition-colors disabled:opacity-50"
              >
                <option value="">Todos los empleados visibles</option>
                {(teamQuery.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fullName} ({u.email})
                  </option>
                ))}
              </select>
            </div>
          </div>
          {downloadError ? (
            <p role="alert" className="mt-3 text-sm text-destructive">
              {downloadError}
            </p>
          ) : null}
        </div>

        {reportQuery.isLoading ? (
          <p className="px-7 py-10 text-center text-sm text-muted-foreground">Cargando…</p>
        ) : reportQuery.error ? (
          <p role="alert" className="px-7 py-10 text-center text-sm text-destructive">
            {reportQuery.error instanceof ApiError
              ? reportQuery.error.message
              : 'Error cargando el reporte'}
          </p>
        ) : (
          <>
            <div className="px-7 pt-4 pb-2 flex items-center justify-between gap-2 flex-wrap">
              <p
                className="font-mono text-[10px] font-medium uppercase text-muted-foreground"
                style={{ letterSpacing: '0.14em' }}
              >
                {rows.length} filas
                {rows.length > previewRows.length ? ` · mostrando ${previewRows.length} primeras` : ''}
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full" style={{ minWidth: 780 }}>
                <thead>
                  <tr>
                    <Th>Empleado</Th>
                    <Th>Fecha</Th>
                    <Th align="right">Esperado</Th>
                    <Th align="right">Trabajado</Th>
                    <Th align="right">Balance</Th>
                    <Th>Estado</Th>
                    <Th>Ausencia</Th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-5 py-10 text-center text-sm text-muted-foreground"
                      >
                        Sin datos para este periodo.
                      </td>
                    </tr>
                  ) : (
                    previewRows.map((r, i) => (
                      <ReportRowView key={`${r.userId}-${r.date}-${i}`} row={r} />
                    ))
                  )}
                </tbody>
                {rows.length > 0 ? (
                  <tfoot>
                    <tr className="bg-paper border-t-2 border-border font-semibold">
                      <td className="px-5 py-3.5 text-[13px]">
                        Total · {rows.length} filas
                        {totals.absences > 0 ? ` · ${totals.absences} con ausencia` : ''}
                      </td>
                      <td />
                      <td className="px-5 py-3.5 text-right font-mono tabular-nums text-[13px]">
                        {formatMinutes(totals.expected)}
                      </td>
                      <td className="px-5 py-3.5 text-right font-mono tabular-nums text-[13px]">
                        {formatMinutes(totals.worked)}
                      </td>
                      <td
                        className={cn(
                          'px-5 py-3.5 text-right font-mono tabular-nums text-[13px]',
                          totals.balance > 0 && 'text-success',
                          totals.balance < 0 && 'text-destructive',
                        )}
                      >
                        {formatMinutesSigned(totals.balance)}
                      </td>
                      <td
                        className={cn(
                          'px-5 py-3.5 text-[13px]',
                          totals.incidents > 0 && 'text-warning',
                        )}
                      >
                        {totals.incidents > 0 ? `${totals.incidents} incid.` : '—'}
                      </td>
                      <td className="px-5 py-3.5 text-[13px]">
                        {totals.absences > 0 ? `${totals.absences}` : '—'}
                      </td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={cn(
        'font-mono text-[12px] font-medium uppercase text-muted-foreground bg-paper border-b border-border px-5 py-3.5',
        align === 'right' ? 'text-right' : 'text-left',
      )}
      style={{ letterSpacing: '0.08em' }}
    >
      {children}
    </th>
  );
}

function ReportRowView({ row }: { row: ReportRow }) {
  const muted = row.isWeekend || row.isHoliday;
  return (
    <tr
      className={cn(
        'border-b border-border hover:bg-paper transition-colors',
        muted && 'bg-paper-warm/40',
      )}
    >
      <td className="px-5 py-3">
        <div className="text-[13px] font-medium">{row.fullName}</div>
        {row.employeeCode ? (
          <div className="font-mono text-[11px] text-muted-foreground">{row.employeeCode}</div>
        ) : null}
      </td>
      <td className="px-5 py-3">
        <div className="font-mono text-[13px] tabular-nums">{row.date}</div>
        {row.isHoliday ? (
          <div className="text-[11px] text-warning">Festivo: {row.holidayName ?? ''}</div>
        ) : row.isWeekend ? (
          <div className="text-[11px] text-muted-foreground">Fin de semana</div>
        ) : null}
      </td>
      <td className="px-5 py-3 text-right font-mono tabular-nums text-[13px]">
        {formatMinutes(row.expectedMinutes)}
      </td>
      <td className="px-5 py-3 text-right font-mono tabular-nums text-[13px]">
        {formatMinutes(row.workedMinutes)}
      </td>
      <td
        className={cn(
          'px-5 py-3 text-right font-mono tabular-nums text-[13px]',
          row.balanceMinutes > 0 && 'text-success',
          row.balanceMinutes < 0 && 'text-destructive',
        )}
      >
        {formatMinutesSigned(row.balanceMinutes)}
      </td>
      <td className="px-5 py-3 text-[12px]">
        {row.status === 'INCIDENT' ? (
          <span className="text-warning font-medium">Incidencia</span>
        ) : row.status === 'CLOSED' ? (
          <span className="text-muted-foreground">Cerrada</span>
        ) : row.status === 'OPEN' ? (
          <span className="text-success">Abierta</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-5 py-3 text-[12px]">
        {row.absenceTypeName ? (
          <span>
            {row.absenceTypeName}
            {row.isHalfDayAbsence ? ` (${row.halfDayPart})` : ''}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
    </tr>
  );
}

function formatMinutes(minutes: number): string {
  if (minutes <= 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function formatMinutesSigned(minutes: number): string {
  if (minutes === 0) return '0h 00m';
  const sign = minutes > 0 ? '+' : '−';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${sign}${h}h ${String(m).padStart(2, '0')}m`;
}
